// src/facturas/UserFacturas.js
// ═══════════════════════════════════════════════════════════════════════════
// RECIBOS DE PAGO DE USUARIO - Estilo unificado basado en UserProfile.js
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import './UserFacturas.css';
import './AdminContabilidad.css';
import '../finanzas/AdminFinanzas.css';
import {
  Container, Row, Col, Card, Table, Button, Modal, Badge, Alert, Spinner
} from 'react-bootstrap';
import {
  FaFileInvoice, FaEye, FaSearch, FaCalendarAlt,
  FaChartLine, FaDollarSign, FaFileAlt, FaPrint,
  FaFilter, FaTimes, FaFileExcel, FaFilePdf
} from 'react-icons/fa';
import {
  FiPieChart, FiTrendingUp, FiDollarSign, FiTarget,
  FiCalendar, FiSettings
} from 'react-icons/fi';
import { API_URL, API_ENDPOINTS } from '../config/api';
import { exportarRecibosExcel } from '../utils/excelExport';

// Importar componentes financieros personales
import FinancialDashboardTab from '../finanzas/FinancialDashboardTab';
import IncomeTab from '../finanzas/IncomeTab';
import ExpenseTab from '../finanzas/ExpenseTab';
import BudgetTab from '../finanzas/BudgetTab';
import AnnualSummaryTab from '../finanzas/AnnualSummaryTab';
import FinancialConfigTab from '../finanzas/FinancialConfigTab';
import FinancialCalendar from '../finanzas/FinancialCalendar';
import FinancialSubmenu, { menuOptions } from '../finanzas/FinancialSubmenu';
import '../finanzas/FinancialSubmenu.css';
import '../finanzas/FinancialCalendar.css';

// ═══════════════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════════════

const formatNumeroRecibo = (num) => {
  if (!num) return 'N/A';
  return num.replace('FAC-', 'RDP-');
};

const getMesFromConcepto = (concepto) => {
  if (!concepto) return '';
  const meses = {
    'enero': 'Ene', 'febrero': 'Feb', 'marzo': 'Mar', 'abril': 'Abr',
    'mayo': 'May', 'junio': 'Jun', 'julio': 'Jul', 'agosto': 'Ago',
    'septiembre': 'Sep', 'octubre': 'Oct', 'noviembre': 'Nov', 'diciembre': 'Dic'
  };
  const lower = concepto.toLowerCase();
  for (const [mes, abrev] of Object.entries(meses)) {
    if (lower.includes(mes)) return abrev;
  }
  return '';
};

const UserFacturas = ({ user }) => {
  // Tab activo
  const [activeTab, setActiveTab] = useState('facturas');

  // Estados para tabs financieros
  const [finYear, setFinYear] = useState(new Date().getFullYear());
  const [finMonth, setFinMonth] = useState(new Date().getMonth() + 1);
  const [notification, setNotification] = useState(null);
  const [showFinancialMenu, setShowFinancialMenu] = useState(false);
  const [financialSubTab, setFinancialSubTab] = useState(null);

  const token = localStorage.getItem('token');

  const [facturas, setFacturas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFactura, setSelectedFactura] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [filtros, setFiltros] = useState({
    fechaInicio: '',
    fechaFin: '',
    busqueda: ''
  });

  // Funcion de notificacion
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  // Estados para metricas
  const [metricas, setMetricas] = useState({
    totalFacturas: 0,
    montoTotal: 0,
    ultimaFactura: null,
    facturasPendientes: 0
  });

  useEffect(() => {
    fetchFacturas();
  }, [user?.id]);

  const fetchFacturas = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      // Obtener recibos del usuario (basado en pagos aprobados)
      const response = await fetch(API_ENDPOINTS.paymentsUser, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Error al cargar recibos');
      }

      const data = await response.json();

      // Filtrar solo pagos aprobados (que tienen recibos generados)
      const pagosAprobados = data.payments.filter(pago =>
        (pago.status === 'Approved' || pago.status === 'Paid') && pago.receipt_path
      );

      // Generar recibos basados en pagos aprobados
      const facturasGeneradas = pagosAprobados.map((pago, index) => {
        const año = new Date().getFullYear();
        const numeroSecuencial = String(pago.id).padStart(3, '0');

        return {
          id: pago.id,
          numero: `RDP-${año}-${numeroSecuencial}`,
          fecha: new Date(pago.payment_date || pago.updated_at || pago.created_at).toLocaleDateString(),
          fechaVencimiento: null,
          concepto: pago.concept,
          monto: parseFloat(pago.amount),
          estado: 'Pagada',
          pagoId: pago.id,
          comprobante: pago.receipt_path,
          fechaAprobacion: pago.payment_date || pago.updated_at
        };
      }).sort((a, b) => new Date(b.fechaAprobacion) - new Date(a.fechaAprobacion));

      setFacturas(facturasGeneradas);
      calcularMetricas(facturasGeneradas);

    } catch (error) {
      console.error('Error al cargar recibos:', error);
      setError('Error al cargar los recibos. Intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const calcularMetricas = (facturas) => {
    const total = facturas.length;
    const monto = facturas.reduce((sum, f) => sum + f.monto, 0);
    const ultima = facturas.length > 0 ? facturas[facturas.length - 1] : null;

    setMetricas({
      totalFacturas: total,
      montoTotal: monto,
      ultimaFactura: ultima,
      facturasPendientes: 0
    });
  };

  const handlePrintRecibo = (factura) => {
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    const reciboContent = document.querySelector('.recibo-preview');
    if (!reciboContent) return;

    const printStyles = `
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body {
          font-family: 'Poppins', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          line-height: 1.6; color: #1e293b; background: white;
        }
        .recibo-preview {
          width: 100%; max-width: none; margin: 0;
          padding: 20mm; background: white;
          box-shadow: none; border: none;
        }
        table { width: 100%; border-collapse: collapse; }
        table th, table td { padding: 8px 12px; border: 1px solid #e2e8f0; }
        table thead tr { background: #4338ca !important; color: #fff !important; }
        table thead th { background: #4338ca !important; color: #fff !important; }
        .urbanq-user-badge { padding: 2px 8px; border-radius: 4px; font-size: 0.8rem; }
        .urbanq-user-badge.success { background: #f0fdf4; color: #16a34a; }
        @media print {
          body { margin: 0; padding: 0; }
          .recibo-preview { padding: 15mm; }
          * { -webkit-print-color-adjust: exact !important; color-adjust: exact !important; }
          table { page-break-inside: avoid; }
        }
        @page { size: A4; margin: 10mm; }
      </style>
    `;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Recibo ${formatNumeroRecibo(factura?.numero) || 'N/A'}</title>
        <meta charset="utf-8">
        ${printStyles}
      </head>
      <body>
        ${reciboContent.outerHTML}
      </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 250);
    };
  };

  const mostrarPreview = (factura) => {
    setSelectedFactura(factura);
    setShowModal(true);
  };

  const formatCurrency = (amount) => {
    return `$${amount.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')}`;
  };

  // Exportar a Excel real (.xlsx)
  const handleExportExcel = () => {
    if (facturas.length === 0) {
      showNotification('No hay recibos para exportar', 'error');
      return;
    }
    try {
      exportarRecibosExcel(facturas, user, 'UrbanQ');
      showNotification('Recibos exportados a Excel exitosamente', 'success');
    } catch (error) {
      console.error('Error exportando Excel:', error);
      showNotification('Error al exportar el reporte', 'error');
    }
  };

  const facturasFiltradas = facturas.filter(factura => {
    const matchBusqueda = factura.numero.toLowerCase().includes(filtros.busqueda.toLowerCase()) ||
                         factura.concepto.toLowerCase().includes(filtros.busqueda.toLowerCase());

    let matchFecha = true;
    if (filtros.fechaInicio && filtros.fechaFin) {
      const fechaFactura = new Date(factura.fecha);
      const inicio = new Date(filtros.fechaInicio);
      const fin = new Date(filtros.fechaFin);
      matchFecha = fechaFactura >= inicio && fechaFactura <= fin;
    }

    return matchBusqueda && matchFecha;
  });

  if (loading) {
    return (
      <div className="facturas-container urbanq-user-container">
        <div className="text-center py-5">
          <Spinner animation="border" role="status" variant="primary" />
          <p className="mt-3">Cargando recibos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="facturas-container urbanq-user-container">
      {/* Notificacion */}
      {notification && (
        <div className={`urbanq-finanzas-notification ${notification.type}`}>
          <span>{notification.message}</span>
          <button onClick={() => setNotification(null)}>&times;</button>
        </div>
      )}

      {/* Tabs de navegacion */}
      <div className="urbanq-user-tabs" style={{ marginTop: '15px', marginBottom: '20px' }}>
        <button
          className={`urbanq-user-tab ${activeTab === 'facturas' ? 'active' : ''}`}
          onClick={() => setActiveTab('facturas')}
        >
          <FaFileInvoice /> Recibos
        </button>

        {/* Tab Mis Finanzas con submenu */}
        <button
          className={`urbanq-user-tab financial-tab ${activeTab === 'finanzas' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('finanzas');
            if (!financialSubTab) {
              setShowFinancialMenu(true);
            }
          }}
        >
          <FiPieChart /> Mis Finanzas
          {financialSubTab && activeTab === 'finanzas' && (
            <span className="financial-tab-indicator"></span>
          )}
        </button>
      </div>

      {/* Submenu Financiero */}
      <FinancialSubmenu
        isOpen={showFinancialMenu}
        onClose={() => setShowFinancialMenu(false)}
        onSelect={(option) => {
          setFinancialSubTab(option);
          setActiveTab('finanzas');
        }}
        currentOption={financialSubTab}
      />

      {/* Contenido segun tab activo */}
      {activeTab === 'facturas' && (
        <>

      {error && (
        <Alert variant="danger" className="mb-4">{error}</Alert>
      )}

      {/* Metricas Dashboard */}
      <div className="urbanq-user-stats-grid">
        <div className="urbanq-user-stat-card primary">
          <div className="urbanq-user-stat-icon primary">
            <FaFileAlt />
          </div>
          <div className="urbanq-user-stat-content">
            <div className="urbanq-user-stat-value">{metricas.totalFacturas}</div>
            <div className="urbanq-user-stat-label">Total Recibos</div>
          </div>
        </div>

        <div className="urbanq-user-stat-card success">
          <div className="urbanq-user-stat-icon success">
            <FaDollarSign />
          </div>
          <div className="urbanq-user-stat-content">
            <div className="urbanq-user-stat-value">{formatCurrency(metricas.montoTotal)}</div>
            <div className="urbanq-user-stat-label">Monto Total Pagado</div>
          </div>
        </div>

        <div className="urbanq-user-stat-card info">
          <div className="urbanq-user-stat-icon info">
            <FaCalendarAlt />
          </div>
          <div className="urbanq-user-stat-content">
            <div className="urbanq-user-stat-value facturas-numero-small">
              {metricas.ultimaFactura ? formatNumeroRecibo(metricas.ultimaFactura.numero) : 'N/A'}
            </div>
            <div className="urbanq-user-stat-label">Ultimo Recibo</div>
          </div>
        </div>

        <div className="urbanq-user-stat-card warning">
          <div className="urbanq-user-stat-icon warning">
            <FaChartLine />
          </div>
          <div className="urbanq-user-stat-content">
            <div className="urbanq-user-stat-value">{new Date().getFullYear()}</div>
            <div className="urbanq-user-stat-label">Ano Actual</div>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="urbanq-user-card mb-4">
        <div className="urbanq-user-card-header">
          <h3><FaFilter /> Filtros de Busqueda</h3>
          <div className="urbanq-export-buttons">
            <button className="urbanq-user-btn secondary sm" onClick={handleExportExcel}>
              <FaFileExcel /> Excel
            </button>
            {(filtros.busqueda || filtros.fechaInicio || filtros.fechaFin) && (
              <button
                className="urbanq-user-btn secondary sm"
                onClick={() => setFiltros({fechaInicio: '', fechaFin: '', busqueda: ''})}
              >
                <FaTimes /> Limpiar
              </button>
            )}
          </div>
        </div>
        <div className="urbanq-user-card-body">
          <Row>
            <Col md={4}>
              <div className="facturas-filtro-grupo">
                <label><FaSearch /> Buscar</label>
                <input
                  type="text"
                  className="facturas-input"
                  placeholder="Numero de recibo o concepto..."
                  value={filtros.busqueda}
                  onChange={(e) => setFiltros({...filtros, busqueda: e.target.value})}
                />
              </div>
            </Col>
            <Col md={4}>
              <div className="facturas-filtro-grupo">
                <label>Fecha Inicio</label>
                <input
                  type="date"
                  className="facturas-input"
                  value={filtros.fechaInicio}
                  onChange={(e) => setFiltros({...filtros, fechaInicio: e.target.value})}
                />
              </div>
            </Col>
            <Col md={4}>
              <div className="facturas-filtro-grupo">
                <label>Fecha Fin</label>
                <input
                  type="date"
                  className="facturas-input"
                  value={filtros.fechaFin}
                  onChange={(e) => setFiltros({...filtros, fechaFin: e.target.value})}
                />
              </div>
            </Col>
          </Row>
        </div>
      </div>

      {/* Lista de Recibos */}
      <div className="urbanq-user-table-card">
        <div className="urbanq-user-table-header">
          <FaFileInvoice />
          <h3>Lista de Recibos ({facturasFiltradas.length})</h3>
        </div>
        <div className="p-0">
          {facturasFiltradas.length === 0 ? (
            <div className="urbanq-user-empty">
              <FaFileInvoice className="urbanq-user-empty-icon" />
              <h4>No hay recibos disponibles</h4>
              <p>Los recibos se generan automaticamente cuando el administrador aprueba tus pagos.</p>
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="urbanq-user-table mb-0">
                <thead>
                  <tr>
                    <th>Numero</th>
                    <th>Fecha</th>
                    <th>Concepto</th>
                    <th>Monto</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {facturasFiltradas.map((factura) => (
                    <tr key={factura.id}>
                      <td>
                        <strong className="text-primary">{formatNumeroRecibo(factura.numero)}</strong>
                      </td>
                      <td>{factura.fecha}</td>
                      <td>{factura.concepto}</td>
                      <td>
                        <strong className="facturas-monto">
                          {formatCurrency(factura.monto)}
                        </strong>
                      </td>
                      <td>
                        <span className="urbanq-user-badge success">{factura.estado}</span>
                      </td>
                      <td>
                        <div className="facturas-acciones">
                          <button
                            className="urbanq-user-btn secondary sm"
                            onClick={() => mostrarPreview(factura)}
                            title="Vista previa"
                          >
                            <FaEye />
                          </button>
                          <button
                            className="urbanq-user-btn primary sm"
                            onClick={() => {
                              mostrarPreview(factura);
                              setTimeout(() => handlePrintRecibo(factura), 500);
                            }}
                            title="Imprimir recibo"
                          >
                            <FaPrint />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </div>
      </div>

      {/* Modal Preview - Recibo de Pago */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" className="facturas-modal">
        <Modal.Header closeButton className="facturas-modal-header">
          <Modal.Title>
            <FaFileInvoice className="me-2" />
            Vista Previa - Recibo {selectedFactura ? formatNumeroRecibo(selectedFactura.numero) : ''}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedFactura && (
            <div className="recibo-preview" style={{
              background: '#fff',
              padding: '30px',
              fontFamily: "'Poppins', 'Segoe UI', sans-serif",
              color: '#1e293b',
              border: '1px solid #e2e8f0',
              borderRadius: '8px'
            }}>
              {/* Header del Recibo */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                borderBottom: '3px solid #4338ca',
                paddingBottom: '16px',
                marginBottom: '20px'
              }}>
                <div>
                  <h4 style={{ color: '#4338ca', margin: 0, fontWeight: 700, fontSize: '1.2rem' }}>
                    {'\u{1F3E2}'} Condominio UrbanQ
                  </h4>
                  <small style={{ color: '#64748b', display: 'block', marginTop: '4px' }}>
                    Quito - Ecuador
                  </small>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <h5 style={{ color: '#4338ca', margin: 0, fontWeight: 700 }}>
                    RECIBO DE PAGO
                  </h5>
                  <div style={{
                    background: '#4338ca',
                    color: '#fff',
                    padding: '4px 12px',
                    borderRadius: '4px',
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    marginTop: '4px',
                    display: 'inline-block'
                  }}>
                    N.{'\u00ba'} {formatNumeroRecibo(selectedFactura.numero)}
                  </div>
                </div>
              </div>

              {/* Datos del Residente */}
              <Row className="mb-3">
                <Col md={6}>
                  <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <h6 style={{ color: '#4338ca', fontWeight: 600, marginBottom: '8px', fontSize: '0.85rem' }}>
                      Datos del Residente
                    </h6>
                    <strong>{user?.nombre || user?.name || 'N/A'}</strong><br />
                    Dpto/Casa: {user?.apartamento || user?.apartment || 'N/A'}<br />
                    Email: {user?.email || 'N/A'}
                  </div>
                </Col>
                <Col md={6}>
                  <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <h6 style={{ color: '#4338ca', fontWeight: 600, marginBottom: '8px', fontSize: '0.85rem' }}>
                      Datos del Recibo
                    </h6>
                    <strong>Numero:</strong> {formatNumeroRecibo(selectedFactura.numero)}<br />
                    <strong>Fecha:</strong> {selectedFactura.fecha}<br />
                    <strong>Estado:</strong>{' '}
                    <span className="urbanq-user-badge success">
                      {selectedFactura.estado}
                    </span>
                  </div>
                </Col>
              </Row>

              {/* Tabla de Conceptos */}
              <Table bordered className="recibo-table" style={{ fontSize: '0.9rem' }}>
                <thead>
                  <tr style={{ background: '#4338ca', color: '#fff' }}>
                    <th>DESCRIPCION</th>
                    <th style={{ width: '80px', textAlign: 'center' }}>MES</th>
                    <th style={{ width: '120px', textAlign: 'right' }}>VALOR UNIT.</th>
                    <th style={{ width: '120px', textAlign: 'right' }}>VALOR TOTAL</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>{selectedFactura.concepto || 'Alicuota Ordinaria'}</strong></td>
                    <td style={{ textAlign: 'center' }}>
                      {getMesFromConcepto(selectedFactura.concepto)}
                    </td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(selectedFactura.monto)}</td>
                    <td style={{ textAlign: 'right' }}><strong>{formatCurrency(selectedFactura.monto)}</strong></td>
                  </tr>
                  <tr style={{ color: '#94a3b8' }}>
                    <td>Alicuota Extraordinaria</td>
                    <td></td><td></td><td></td>
                  </tr>
                  <tr style={{ color: '#94a3b8' }}>
                    <td>Parqueadero</td>
                    <td></td><td></td><td></td>
                  </tr>
                  <tr style={{ color: '#94a3b8' }}>
                    <td>Agua</td>
                    <td></td><td></td><td></td>
                  </tr>
                  <tr style={{ color: '#94a3b8' }}>
                    <td>Otros</td>
                    <td></td><td></td><td></td>
                  </tr>
                </tbody>
              </Table>

              {/* Totales */}
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <div style={{
                  width: '280px',
                  border: '2px solid #4338ca',
                  borderRadius: '8px',
                  overflow: 'hidden'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 14px', borderBottom: '1px solid #e2e8f0' }}>
                    <span>SUBTOTAL USD:</span>
                    <strong>{formatCurrency(selectedFactura.monto)}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 14px', borderBottom: '1px solid #e2e8f0' }}>
                    <span>I.V.A.:</span>
                    <span>$0.00</span>
                  </div>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '10px 14px',
                    background: '#4338ca',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: '1.1rem'
                  }}>
                    <span>TOTAL USD:</span>
                    <span>{formatCurrency(selectedFactura.monto)}</span>
                  </div>
                </div>
              </div>

              {/* Footer - Firmas */}
              <div style={{
                marginTop: '20px',
                paddingTop: '16px',
                borderTop: '1px dashed #cbd5e1',
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '0.8rem',
                color: '#94a3b8'
              }}>
                <div style={{ textAlign: 'center', width: '40%' }}>
                  <div style={{ borderTop: '1px solid #cbd5e1', paddingTop: '4px', marginTop: '30px' }}>
                    ADMINISTRACION
                  </div>
                </div>
                <div style={{ textAlign: 'center', width: '40%' }}>
                  <div style={{ borderTop: '1px solid #cbd5e1', paddingTop: '4px', marginTop: '30px' }}>
                    FIRMA CONDOMINO
                  </div>
                </div>
              </div>

              <div style={{ textAlign: 'center', marginTop: '16px', fontSize: '0.75rem', color: '#94a3b8' }}>
                Generado electronicamente por UrbanQ &mdash; {new Date().toLocaleDateString('es-EC')}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button className="urbanq-user-btn secondary" onClick={() => setShowModal(false)}>
            Cerrar
          </button>
          <button
            className="urbanq-user-btn primary"
            onClick={() => handlePrintRecibo(selectedFactura)}
          >
            <FaPrint /> Imprimir
          </button>
        </Modal.Footer>
      </Modal>
        </>
      )}

      {/* TAB FINANZAS UNIFICADO */}
      {activeTab === 'finanzas' && (
        <div className="urbanq-finanzas-wrapper">
          {/* Breadcrumb cuando hay subtab seleccionado */}
          {financialSubTab && (
            <div className="financial-breadcrumb">
              <span>Mis Finanzas</span>
              <span className="financial-breadcrumb-separator">/</span>
              <span className="financial-breadcrumb-current">
                {menuOptions.find(o => o.id === financialSubTab)?.title || ''}
              </span>
              <button
                className="financial-breadcrumb-back"
                onClick={() => setShowFinancialMenu(true)}
              >
                Cambiar
              </button>
            </div>
          )}

          {/* Estado vacio cuando no hay subtab */}
          {!financialSubTab && (
            <div className="urbanq-finanzas-empty-state">
              <FiPieChart className="empty-icon" />
              <h3>Selecciona una opcion</h3>
              <p>Haz clic en el boton de abajo para elegir que deseas gestionar</p>
              <button
                className="urbanq-finanzas-btn primary"
                onClick={() => setShowFinancialMenu(true)}
              >
                Abrir Menu Financiero
              </button>
            </div>
          )}

          {/* Panel Financiero */}
          {financialSubTab === 'panel_financiero' && (
            <>
              <div className="urbanq-finanzas-year-selector">
                <label>Ano:</label>
                <select value={finYear} onChange={(e) => setFinYear(parseInt(e.target.value))}>
                  {[2023, 2024, 2025, 2026].map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
                <label>Mes:</label>
                <select value={finMonth} onChange={(e) => setFinMonth(parseInt(e.target.value))}>
                  {['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'].map((m, i) => (
                    <option key={i} value={i + 1}>{m}</option>
                  ))}
                </select>
              </div>
              <FinancialDashboardTab
                year={finYear}
                month={finMonth}
                token={token}
                apiUrl={API_URL}
                scope="personal"
                onNotification={showNotification}
              />
            </>
          )}

          {/* Ingresos */}
          {financialSubTab === 'mis_ingresos' && (
            <>
              <div className="urbanq-finanzas-year-selector">
                <label>Ano:</label>
                <select value={finYear} onChange={(e) => setFinYear(parseInt(e.target.value))}>
                  {[2023, 2024, 2025, 2026].map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
                <label>Mes:</label>
                <select value={finMonth} onChange={(e) => setFinMonth(parseInt(e.target.value))}>
                  {['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'].map((m, i) => (
                    <option key={i} value={i + 1}>{m}</option>
                  ))}
                </select>
              </div>
              <IncomeTab
                year={finYear}
                month={finMonth}
                token={token}
                apiUrl={API_URL}
                scope="personal"
                onNotification={showNotification}
              />
            </>
          )}

          {/* Gastos */}
          {financialSubTab === 'mis_gastos' && (
            <>
              <div className="urbanq-finanzas-year-selector">
                <label>Ano:</label>
                <select value={finYear} onChange={(e) => setFinYear(parseInt(e.target.value))}>
                  {[2023, 2024, 2025, 2026].map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
                <label>Mes:</label>
                <select value={finMonth} onChange={(e) => setFinMonth(parseInt(e.target.value))}>
                  {['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'].map((m, i) => (
                    <option key={i} value={i + 1}>{m}</option>
                  ))}
                </select>
              </div>
              <ExpenseTab
                year={finYear}
                month={finMonth}
                token={token}
                apiUrl={API_URL}
                scope="personal"
                onNotification={showNotification}
              />
            </>
          )}

          {/* Presupuesto */}
          {financialSubTab === 'presupuesto' && (
            <>
              <div className="urbanq-finanzas-year-selector">
                <label>Ano:</label>
                <select value={finYear} onChange={(e) => setFinYear(parseInt(e.target.value))}>
                  {[2023, 2024, 2025, 2026].map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
              <BudgetTab
                year={finYear}
                month={finMonth}
                token={token}
                apiUrl={API_URL}
                scope="personal"
                onNotification={showNotification}
              />
            </>
          )}

          {/* Resumen Anual */}
          {financialSubTab === 'resumen_anual' && (
            <>
              <div className="urbanq-finanzas-year-selector">
                <label>Ano:</label>
                <select value={finYear} onChange={(e) => setFinYear(parseInt(e.target.value))}>
                  {[2023, 2024, 2025, 2026].map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
              <AnnualSummaryTab
                year={finYear}
                token={token}
                apiUrl={API_URL}
                scope="personal"
                onNotification={showNotification}
              />
            </>
          )}

          {/* Calendario */}
          {financialSubTab === 'calendario' && (
            <FinancialCalendar
              token={token}
              apiUrl={API_URL}
              scope="personal"
              onNotification={showNotification}
              userId={user?.id}
            />
          )}

          {/* Configuracion */}
          {financialSubTab === 'config_financiera' && (
            <FinancialConfigTab
              token={token}
              apiUrl={API_URL}
              scope="personal"
              onNotification={showNotification}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default UserFacturas;
